import React, { ReactNode } from 'react';
import { useAccessControl } from '../contexts/AccessControlContext';
import { Navigate, useLocation } from 'react-router-dom';
import { Clock, Shield } from 'lucide-react';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { token, isValid, isExpired, isLoading, remainingTime } = useAccessControl();
  const location = useLocation();
  const isDemoRoute =
    location.pathname === '/demo' || location.pathname.startsWith('/demo/');

  if (isDemoRoute) {
    return (
      <div className="min-h-screen bg-primary-bg">
        <div>{children}</div>
      </div>
    );
  }

  // Show loading while checking token
  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gold border-t-transparent mx-auto mb-4"></div>
          <p className="text-gold">Validating access...</p>
        </div>
      </div>
    );
  }

  // Redirect to access page if no token
  if (!token) {
    return <Navigate to="/access" replace />;
  }

  // Redirect to expired page if token is expired
  if (isExpired) {
    return <Navigate to="/access-expired" replace />;
  }

  // Redirect to access page if token is invalid
  if (!isValid) {
    return <Navigate to="/access" replace />;
  }

  // Show access warning if time is running low
  const isTimeRunningLow = remainingTime < 60; // Less than 1 minute

  return (
    <div className="min-h-screen bg-primary-bg">
      {/* Access Status Banner */}
      {isTimeRunningLow && (
        <div className="bg-yellow-900 bg-opacity-20 border-b border-yellow-500 p-3">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={20} className="text-yellow-400" />
              <span className="text-yellow-400 font-semibold">
                Access expires in {Math.floor(remainingTime / 60)}m {remainingTime % 60}s
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Shield size={16} className="text-yellow-400" />
              <span className="text-yellow-300 text-sm">Limited Time Access</span>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={isTimeRunningLow ? 'pt-0' : ''}>
        {children}
      </div>
    </div>
  );
};

export default ProtectedRoute;
